import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

import Link from 'components/UI/Link/Link';

import styles from './Pagination.module.scss';

export function Pagination({
  className,
  ...props
}: ComponentProps<'nav'>): JSX.Element {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={classNames(className)}
      {...props}
    />
  );
}

export function PaginationContent({
  className,
  ...props
}: ComponentProps<'ul'>): JSX.Element {
  return <ul className={classNames(styles.items, className)} {...props} />;
}

export function PaginationItem({
  ...props
}: ComponentProps<'li'>): JSX.Element {
  return <li {...props} />;
}

type PaginationLinkProps = { isActive?: boolean } & ComponentProps<typeof Link>;

export function PaginationLink({
  className,
  isActive,
  ...props
}: PaginationLinkProps): JSX.Element {
  return (
    <Link
      className={classNames(styles.item, className)}
      aria-current={isActive ? 'page' : undefined}
      {...props}
    />
  );
}

export function PaginationPrevious({
  ...props
}: ComponentProps<typeof PaginationLink>): JSX.Element {
  const { t } = useTranslation();
  return (
    <PaginationLink rel="prev" aria-label={t('PREVIOUS_PAGE')} {...props}>
      <NavigateBeforeIcon fontSize={'inherit'} />
    </PaginationLink>
  );
}

export function PaginationNext({
  ...props
}: ComponentProps<typeof PaginationLink>): JSX.Element {
  const { t } = useTranslation();
  return (
    <PaginationLink rel="next" aria-label={t('NEXT_PAGE')} {...props}>
      <NavigateNextIcon fontSize={'inherit'} />
    </PaginationLink>
  );
}

export function PaginationEllipsis({
  className,
  ...props
}: ComponentProps<'span'>): JSX.Element {
  return (
    <span
      aria-hidden
      className={classNames(styles.ellipsisItem, className)}
      {...props}
    >
      <MoreHorizIcon fontSize={'inherit'} />
    </span>
  );
}
